import type { HTMLAttributes } from "react";

export type SkeletonShape = "table-row" | "detail-field";

const skeletonBlocksByShape = {
  "detail-field": [
    "marker-skeleton__block--detail-label",
    "marker-skeleton__block--detail-value",
  ],
  "table-row": [
    "marker-skeleton__block--table-primary",
    "marker-skeleton__block--table-secondary",
    "marker-skeleton__block--table-tertiary",
    "marker-skeleton__block--table-quaternary",
  ],
} as const satisfies Record<SkeletonShape, readonly string[]>;

export interface SkeletonProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  // Fill mode (§4b.3): the skeleton joins a workspace flex column, stretches
  // to the remaining viewport height, and renders cover rows cropped by its
  // own overflow — `rows` is ignored.
  fill?: boolean;
  label?: string;
  rows?: number;
  shape: SkeletonShape;
}

// Enough rows to cover any plausible viewport in fill mode (rows are 40px
// tall); the overflow crop hides the excess.
const fillRowCount = 64;

function getRowCount(rows: number): number {
  if (!Number.isFinite(rows)) {
    return 1;
  }

  return Math.max(1, Math.floor(rows));
}

export function Skeleton({
  className,
  fill = false,
  label = "Loading",
  rows = 1,
  shape,
  ...props
}: SkeletonProps) {
  const classes = ["marker-skeleton", `marker-skeleton--${shape}`];
  if (fill) {
    classes.push("marker-skeleton--fill");
  }
  if (className) {
    classes.push(className);
  }

  const blockClasses = skeletonBlocksByShape[shape];
  const rowCount = fill ? fillRowCount : getRowCount(rows);

  return (
    <div
      aria-busy="true"
      aria-label={label}
      className={classes.join(" ")}
      role="status"
      {...props}
    >
      {Array.from({ length: rowCount }, (_, rowIndex) => (
        <div aria-hidden="true" className="marker-skeleton__row" key={rowIndex}>
          {blockClasses.map((blockClass) => (
            <span
              className={`marker-skeleton__block ${blockClass}`}
              key={blockClass}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
